import { type FC, useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useMeasure from 'react-use-measure';
import cn from 'classnames';
import {
  animate,
  type AnimationPlaybackControls,
  motion,
  useMotionTemplate,
  useMotionValue,
} from 'framer-motion';

import { parseMeasureFromCssVars } from '@/common/lib/measure-to-css-vars';
import { type MiniGameType } from '@/common/types/dice-game';
import { ClaimCashCardAnimation } from '@/components/Animations/ClaimItemAnimation/ClaimCashCardAnimation/ClaimCashCardAnimation';
import { CounterUpDown } from '@/components/CounterUpDown/CounterUpDown';
import { MotionPicture } from '@/components/ui/Picture/Picture';
import { setTabBarHighlight } from '@/redux/reducers/appReducer';
import {
  setActiveBoard,
  setClaimRewardAnimationFinalPosition,
} from '@/redux/reducers/diceGameReducer';
import { useAppDispatch } from '@/redux/store';

import { DiceGameContext } from '../context';

import cashPng from '/img/icons/cash-large.png';
import cashWebp from '/img/icons/cash-large.webp';
import starPng from '/img/icons/star-large.png';
import starWebp from '/img/icons/star-large.webp';
import tgemPng from '/img/tgem.png';
import tgemWebp from '/img/tgem.webp';

interface RewardAnimationProps {
  kind?: MiniGameType;
  value?: number;
  className?: string;
  duration?: number;
  delay?: number;
  hidden?: boolean;
  scale?: number;
}

const RewardAnimation: FC<RewardAnimationProps> = ({
  className,
  kind,
  value,
  delay = 700,
  duration = 1.1,
  hidden,
  scale,
}) => {
  const {
    setCardsRewardFromRef,
    flyingAnimation,
    setFlyingAnimation,
    setIsRewardScaled,
  } = useContext(DiceGameContext);
  const pictureRef = useRef<HTMLElement | null>(null);
  const [ref, measure] = useMeasure({ offsetSize: true, debounce: 500 });
  const blur = useMotionValue(0);
  const colors = {
    cash: 'rgb(130, 224, 36)',
    str: '#FFD600',
    tgem: '#5850D2',
  };

  const filter = useMotionTemplate`drop-shadow(0px 0px ${blur}px ${kind && kind in colors ? colors[kind as keyof typeof colors] : colors.cash})`;

  useEffect(() => {
    let controlls: AnimationPlaybackControls | undefined;

    if (typeof value === 'number') {
      controlls = animate(blur, [0, 4, 4, 0], {
        delay: 0.7,
        duration: 1.1,
        ease: 'easeOut',
        times: [0, 0.2, 0.8, 1],
      });
    }

    return (): void => {
      controlls?.stop();
    };
  }, [value]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const reward = kind?.toLowerCase();

    if (measure.x && measure.y && reward) {
      dispatch(
        setClaimRewardAnimationFinalPosition({
          reward: reward as 'cash' | 'tgem' | 'str',
          position: measure,
        }),
      );
    }
  }, [measure]);

  useEffect(() => {
    setCardsRewardFromRef(pictureRef);
  }, []);

  const images = {
    cash: {
      png: cashPng,
      webp: cashWebp,
    },
    str: {
      png: starPng,
      webp: starWebp,
    },
    tgem: {
      png: tgemPng,
      webp: tgemWebp,
    },
  };

  if (!kind) {
    return null;
  }

  const image =
    kind in images ? images[kind as keyof typeof images] : undefined;

  const officePosition = parseMeasureFromCssVars('--tabbar-office');

  return (
    <motion.div
      className={cn('flex items-center gap-3 h-10', className)}
      animate={{
        opacity: hidden ? 0 : 1,
      }}
      transition={{ duration: 0.2 }}
    >
      {image && (
        <>
          <div
            className="w-10 h-10 shrink-0 relative"
            ref={(element) => {
              pictureRef.current = element;
              ref(element);
            }}
          >
            <MotionPicture
              className="h-full w-full block"
              img={image.png}
              webp={image.webp}
              alt={kind}
              animate={{
                opacity: hidden ? 0 : 1,
                scale,
              }}
              transition={{
                duration: 0.3,
              }}
            />
            {flyingAnimation === 'star' &&
              officePosition &&
              createPortal(
                <div
                  className="absolute z-[9999]"
                  style={{
                    ...measure,
                  }}
                >
                  <ClaimCashCardAnimation
                    icon={{ img: image?.png, webp: image?.webp }}
                    reward={'str'}
                    finalPositon={officePosition}
                    type="arc"
                    onAnimationEnd={() => {
                      setFlyingAnimation(null);
                      dispatch(setActiveBoard('dice'));
                      setFlyingAnimation(null);
                      setIsRewardScaled(false);
                      dispatch(setTabBarHighlight(null));
                    }}
                    startScale={scale}
                    scale={[1, 1, 1, 1, 0.5]}
                  />
                </div>,
                document.getElementById('root')!,
              )}
          </div>
          <motion.p
            style={{ filter }}
            className="text-[2rem] leading-none font-extrabold"
          >
            <CounterUpDown
              value={Number.isInteger(value) ? value! : 0}
              delay={delay}
              duration={duration}
              key={`${delay}-${duration}`}
            />
          </motion.p>
        </>
      )}
    </motion.div>
  );
};

export { RewardAnimation };
